/*
 * ERROR BOUNDARY COMPONENT
 * =======================
 * Catches JavaScript errors anywhere in the component tree and displays
 * a fallback UI instead of crashing the entire application.
 * 
 * WHAT THIS COMPONENT DOES:
 * 1. Catches runtime errors in React component tree
 * 2. Logs detailed error information for debugging
 * 3. Shows user-friendly error messages
 * 4. Provides recovery actions (refresh, go back)
 * 5. Integrates with Mantine UI for consistent styling
 * 
 * USAGE:
 * - Wrap entire app: <ErrorBoundary><App /></ErrorBoundary>
 * - Wrap page routes: <ErrorBoundary><ProjectList /></ErrorBoundary>
 * - Wrap critical components: <ErrorBoundary><DataTable /></ErrorBoundary>
 */

import React, { Component, ReactNode } from 'react';
import { Container, Title, Text, Button, Stack, Alert, Code, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorId: string;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    };
  }

  /*
   * REACT ERROR BOUNDARY LIFECYCLE
   * ==============================
   * Called when an error occurs in any child component
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Generate unique error ID for tracking
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorId
    };
  }

  /*
   * ERROR LOGGING AND REPORTING
   * ===========================
   * Called after an error has been caught
   */
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update state with full error info
    this.setState({ errorInfo });
    
    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log comprehensive error information
    console.group('🚨 React Error Boundary Caught Error');
    console.error('Error ID:', this.state.errorId);
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.error('Error Stack:', error.stack);
    console.groupEnd();

    // Show user notification
    notifications.show({
      title: '❌ Application Error',
      message: 'An unexpected error occurred. Please try refreshing the page.',
      color: 'red',
      autoClose: false
    });

    // TODO: In production, send error to logging service
    // this.reportErrorToService(error, errorInfo, this.state.errorId);
  }

  /*
   * ERROR RECOVERY ACTIONS
   * =====================
   * Provide users with ways to recover from errors
   */
  handleRefresh = () => {
    window.location.reload();
  };

  handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: ''
    });
  };

  /*
   * RENDER ERROR UI
   * ==============
   * Shows user-friendly error message with recovery options
   */
  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Container size="md" py="xl">
          <Stack spacing="lg" align="center">
            {/* Error Icon and Title */}
            <div style={{ fontSize: '4rem', textAlign: 'center' }}>🚨</div>
            <Title order={1} ta="center" c="red">
              Oops! Something went wrong
            </Title>
            
            <Text ta="center" size="lg" c="dimmed">
              An unexpected error occurred while loading this page. 
              Don't worry - your data is safe and this has been logged for our team.
            </Text>

            {/* Error Alert */}
            <Alert 
              variant="light" 
              color="red" 
              title="Error Details"
              style={{ width: '100%', maxWidth: '600px' }}
            >
              <Stack spacing="xs">
                <Text size="sm">
                  <strong>Error ID:</strong> {this.state.errorId}
                </Text>
                <Text size="sm">
                  <strong>Error:</strong> {this.state.error?.message || 'Unknown error'}
                </Text>
                {this.state.error?.stack && (
                  <details>
                    <summary style={{ cursor: 'pointer', marginTop: '8px' }}>
                      Technical Details (Click to expand)
                    </summary>
                    <Code block mt="xs" style={{ fontSize: '0.8rem', maxHeight: '200px', overflow: 'auto' }}>
                      {this.state.error.stack}
                    </Code>
                  </details>
                )}
              </Stack>
            </Alert>

            {/* Recovery Actions */}
            <Group>
              <Button 
                onClick={this.handleRefresh}
                color="blue"
                size="lg"
              >
                🔄 Refresh Page
              </Button>
              
              <Button 
                onClick={this.handleGoBack}
                variant="outline"
                color="gray"
                size="lg"
              >
                ← Go Back
              </Button>
              
              <Button 
                onClick={this.handleReset}
                variant="subtle"
                color="gray"
                size="lg"
              >
                🔧 Try Again
              </Button>
            </Group>

            {/* Help Information */}
            <Alert 
              variant="light" 
              color="blue" 
              title="Need Help?"
              style={{ width: '100%', maxWidth: '600px' }}
            >
              <Text size="sm">
                If this problem persists, please:
              </Text>
              <ul style={{ fontSize: '0.875rem', marginTop: '8px' }}>
                <li>Try refreshing the page or clearing your browser cache</li>
                <li>Check your internet connection</li>
                <li>Contact support with Error ID: <Code>{this.state.errorId}</Code></li>
              </ul>
            </Alert>
          </Stack>
        </Container>
      );
    }

    // No error - render children normally
    return this.props.children;
  }

  /*
   * FUTURE: ERROR REPORTING SERVICE
   * ===============================
   * Send errors to external logging service in production
   */
  // private reportErrorToService(error: Error, errorInfo: React.ErrorInfo, errorId: string) {
  //   // TODO: Implement error reporting to service like Sentry, LogRocket, etc.
  //   // Example:
  //   // Sentry.captureException(error, {
  //   //   contexts: {
  //   //     react: {
  //   //       componentStack: errorInfo.componentStack,
  //   //       errorId
  //   //     }
  //   //   }
  //   // });
  // }
}

export default ErrorBoundary;

/*
 * SPECIALIZED ERROR BOUNDARIES
 * ============================
 * Pre-configured error boundaries for specific use cases
 */

// Route-level error boundary
export const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    onError={(error, errorInfo) => {
      console.log('Route error:', error, errorInfo);
    }}
  >
    {children}
  </ErrorBoundary>
);

// API/Data error boundary with retry
export const DataErrorBoundary: React.FC<{ children: ReactNode; onRetry?: () => void }> = ({ 
  children, 
  onRetry 
}) => (
  <ErrorBoundary
    fallback={
      <Container size="sm" py="xl">
        <Stack spacing="md" align="center">
          <div style={{ fontSize: '3rem' }}>📡</div>
          <Title order={2} ta="center">Connection Problem</Title>
          <Text ta="center" c="dimmed">
            Unable to load data from the server.
          </Text>
          <Group>
            <Button onClick={onRetry} color="blue">
              🔄 Retry
            </Button>
            <Button onClick={() => window.location.reload()} variant="outline">
              Refresh Page
            </Button>
          </Group>
        </Stack>
      </Container>
    }
  >
    {children}
  </ErrorBoundary>
);